import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { getFormSessionDetailsTagStyles, getTagBorderStyles } from 'common/entities/form/styleConfiguration/selectors';
import transformValueToPixels from 'common/compounds/FormRenderer/utils/transformValueToPixels';

import { withFormId } from '../FormIdContext';

const Container = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    overflow: hidden;
    max-width: 20.625rem;
    gap: 0.5rem;
`;

const TagWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ borderRadius }) => transformValueToPixels(borderRadius)};
    border-color: ${({ borderColor }) => borderColor};
    background-color: ${({ backgroundColor }) => backgroundColor};
    border-style: solid;
    border-top-width: ${({ borderWidth }) => transformValueToPixels(borderWidth.sizes.top)};
    border-right-width: ${({ borderWidth }) => transformValueToPixels(borderWidth.sizes.right)};
    border-bottom-width: ${({ borderWidth }) => transformValueToPixels(borderWidth.sizes.bottom)};
    border-left-width: ${({ borderWidth }) => transformValueToPixels(borderWidth.sizes.left)};
    padding: 4px 8px;
    max-width: 26%;
`;

const Tag = styled.span`
    font-family: ${({ fontFamily }) => fontFamily};
    font-size: ${({ fontSize }) => transformValueToPixels(fontSize)};
    color: ${({ color }) => color};
    line-height: 1.41;
    letter-spacing: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const FormSessionTags = ({ formId, tags }) => {
    const { font, fontSize, color, background } = useSelector((state) => getFormSessionDetailsTagStyles(state, formId));
    const {
        color: borderColor,
        radius: borderRadius,
        ...borderWidth
    } = useSelector((state) => getTagBorderStyles(state, formId));
    const displayedTags = tags.slice(0, 3);
    const overflowCount = Math.max(tags.length - 3, 0);

    if (!tags || tags.length === 0) {
        return null;
    }

    return (
        <Container aria-label="Tags">
            {displayedTags.map((tag) => (
                <TagWrapper
                    key={tag}
                    backgroundColor={background.value}
                    borderRadius={borderRadius}
                    borderWidth={borderWidth}
                    borderColor={borderColor.value}
                >
                    <Tag fontFamily={font.value} fontSize={fontSize} color={color.value}>
                        {tag}
                    </Tag>
                </TagWrapper>
            ))}
            {overflowCount > 0 && (
                <>
                    <VisuallyHidden.Root>{`+${overflowCount} more tags`}</VisuallyHidden.Root>
                    <TagWrapper
                        backgroundColor={background.value}
                        borderRadius={borderRadius}
                        borderWidth={borderWidth}
                        borderColor={borderColor.value}
                    >
                        <Tag fontFamily={font.value} fontSize={fontSize} color={color.value} aria-hidden>
                            + {overflowCount}
                        </Tag>
                    </TagWrapper>
                </>
            )}
        </Container>
    );
};

FormSessionTags.propTypes = {
    formId: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default withFormId(FormSessionTags);
